<template>
  <div
    v-if="visibleForms.length > 0"
    class="settings-section"
  >
    <TriggerCardSettingRow
      v-for="form in visibleForms"
      :key="form.eventDetailKey"
      :label="i18n.t(`trigger.eventSetting.cardLabels.${form.inputType}`)"
    >
      <template v-if="form.inputType === 'sequence_select'">
        <SequenceMasterSimpleSelect
          :model-value="numberValueOf(form)"
          is-display-only
          hide-link
          wait-key-suffix="CardSettings"
        />
      </template>
      <template v-else-if="form.inputType === 'mail_type_sequence_step_select'">
        <SequenceStepMasterSimpleSelect
          :key="belongsToValueOf(form)"
          :model-value="numberValueOf(form)"
          :sequence-master-id="belongsToValueOf(form)"
          :condition="{ stepType: 'direct_mail' }"
          :required="false"
          is-display-only
          wait-key-suffix="CardSettings"
        />
      </template>
      <template v-else-if="form.inputType === 'multi_text_input'">
        <div
          v-for="(url, index) in urlsOf(form)"
          :key="index"
        >
          {{ url }}
        </div>
      </template>
      <template v-else-if="form.inputType === 'custom_field_select'">
        <CustomFieldSimpleSelect
          :model-value="numberValueOf(form)"
          :required="false"
          is-display-only
          wait-key-suffix="CardSettings"
        />
      </template>
      <template v-else-if="form.inputType === 'opportunity_stage_select'">
        <OpportunityStageSimpleSelect
          :model-value="numberValueOf(form)"
          :required="false"
          is-display-only
          wait-key-suffix="CardSettings"
        />
      </template>
      <template v-else-if="form.inputType === 'call_result_type_select'">
        <CallResultTypeSelect
          :model-value="stringValueOf(form)"
          :required="false"
          is-display-only
          wait-key-suffix="CardSettings"
        />
      </template>
      <template v-else-if="form.inputType === 'boolean_input'">
        <span>{{ booleanLabelOf(form) }}</span>
      </template>
    </TriggerCardSettingRow>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { TriggerEventBody, TriggerEventForm } from '@/api/openapi';
import CallResultTypeSelect from './CallResultTypeSelect.vue';
import CustomFieldSimpleSelect from './CustomFieldSimpleSelect.vue';
import OpportunityStageSimpleSelect from './OpportunityStageSimpleSelect.vue';
import SequenceMasterSimpleSelect from './SequenceMasterSimpleSelect.vue';
import SequenceStepMasterSimpleSelect from './SequenceStepMasterSimpleSelect.vue';
import TriggerCardSettingRow from './TriggerCardSettingRow.vue';

type TProps = {
  triggerEvent: TriggerEventBody | null;
  forms: TriggerEventForm[];
};
const props = defineProps<TProps>();

const i18n = useI18n();

// eventDetail は OpenAPI 上 object 型（キー不定のバッグ）。index アクセスのため
// Record<string, unknown> として扱い、値は表示時に各 model 型へ絞る。
// キャストは vue-eslint-parser が template 内の union 型をフィルタ構文と誤認するため、
// template ではなく script 側のゲッターで行う。
const eventDetail = computed(() => {
  const detail = props.triggerEvent?.eventDetail ?? {};
  return detail as Record<string, unknown>;
});

const numberValueOf = (form: TriggerEventForm) =>
  eventDetail.value[form.eventDetailKey] as number | null;

const stringValueOf = (form: TriggerEventForm) =>
  eventDetail.value[form.eventDetailKey] as string | null;

const belongsToValueOf = (form: TriggerEventForm) => {
  if (!form.eventDetailBelongsToKey) return null;
  return eventDetail.value[form.eventDetailBelongsToKey] as number | null;
};

const urlsOf = (form: TriggerEventForm) => {
  const value = eventDetail.value[form.eventDetailKey];
  return Array.isArray(value) ? value as string[] : [];
};

const booleanLabelOf = (form: TriggerEventForm) =>
  i18n.t(`customField.boolean.${String(eventDetail.value[form.eventDetailKey])}`);

// 設定済み（値が入っている）項目だけを、forms の順序で表示する。
const hasValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value !== '';
  return true;
};

const visibleForms = computed(() =>
  props.forms.filter((form) => hasValue(eventDetail.value[form.eventDetailKey])),
);
</script>

<style lang="scss" scoped>
.settings-section {
  padding: 12px 20px;
  border-top: 1px solid $bdcolor-light;
}
</style>
